import React from "react";
import { connect, ConnectedProps } from "react-redux";
import Loader from "components/Loader";
import { State } from "state";
import { filterUsers } from "state/users";
import { User } from "interfaces/user";
import SEARCH_ICON from "assets/img/filter-data.svg";

/* ---------------- Types ---------------- */
interface Option {
  value: number;
  label: string;
  email?: string;
  user?: User;
}

interface CopyUserFilterOwnProps {
  dataQa?: string;
  onChange: (option: { id: number; name: string; user: User | null }) => void;
}

type Props = CopyUserFilterOwnProps & PropsFromRedux;

interface StateLocal {
  searching: boolean;
  options: Option[];
  selected: Option | null;
  isOpen: boolean;
  searchText: string;
}

let _copySearchTimer: ReturnType<typeof setTimeout> | null = null;

class CopyUserFilter extends React.PureComponent<Props, StateLocal> {
  inputRef = React.createRef<HTMLInputElement>();
  state: StateLocal = {
    searching: false,
    options: [],
    selected: null,
    isOpen: false,
    searchText: "",
  };

  componentDidUpdate(prevProps: Props) {
    if (this.props.users !== prevProps.users) {
      const options: Option[] = (this.props.users || []).map((u: any) => ({
        value: u.id,
        label: `${u.firstname} ${u.lastname}`,
        email: u.email,
        user: u,
      }));
      this.setState({ options, searching: false, isOpen: options.length > 0 });
    }
  }

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    this.setState({ searchText: value, selected: null });

    if (_copySearchTimer) clearTimeout(_copySearchTimer);

    if (!value.trim()) {
      this.setState({ options: [], isOpen: false, searching: false });
      return;
    }

    this.setState({ searching: true });
    _copySearchTimer = setTimeout(() => {
      this.props.filterUsers({ email: value.trim(), page: 1, limit: 50 });
    }, 300);
  };

  handleSelect = (option: Option) => {
    this.props.onChange({
      id: option.value,
      name: option.label,
      user: option.user || null,
    });

    this.setState(
      { selected: null, searchText: "", isOpen: false, options: [] },
      () => { this.inputRef.current?.focus(); }
    );
  };

  render() {
    const { dataQa } = this.props;
    const { searching, selected, isOpen, searchText, options } = this.state;
    const lineSeparatorCss: React.CSSProperties = {
      height: "1px",
      width: "100%",
      backgroundColor: "#53598D26",
      margin: "5px auto",
    };

    return (
      <div
        style={{ position: "relative", width: "100%" }}
        data-qa={dataQa}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input field */}
        <div style={{ position: "relative" }}>
          <input
            ref={this.inputRef}
            type="text"
            placeholder="Search by name or email"
            value={searchText}
            onChange={this.handleInputChange}
            style={{
              width: "100%",
              padding: "8px 36px 8px 12px",
              border: "1px solid #ccc",
              outline: "none",
              cursor: "text",
              borderRadius: "8px",
            }}
          />
          {searching ? (
            <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}>
              <Loader height={14} width={14} custom />
            </span>
          ) : (
            <img
              src={SEARCH_ICON}
              alt="search"
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 14,
                opacity: 0.6,
                pointerEvents: "none",
              }}
            />
          )}
        </div>

        {/* Dropdown */}
        {isOpen && options.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "40px",
              width: "100%",
              maxHeight: "240px",
              overflowY: "auto",
              border: "1px solid #ddd",
              background: "#fff",
              zIndex: 10,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              borderRadius: 4,
            }}
          >
            {options.map((opt) => {
              const isSelected = selected?.value === opt.value;
              return (
                <div key={opt.value}>
                  <div
                    onClick={() => this.handleSelect(opt)}
                    style={{
                      padding: "10px 12px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#FDF19EC2" : "transparent",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#FDF19EC2")}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = isSelected ? "#FDF19EC2" : "transparent")
                    }
                  >
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{opt.email}</div>
                  </div>
                  <div style={lineSeparatorCss} />
                </div>
              );
            })}
          </div>
        )}

        {/* No results */}
        {isOpen && !searching && searchText.trim() && options.length === 0 && (
          <div
            style={{
              position: "absolute",
              top: "40px",
              width: "100%",
              border: "1px solid #ddd",
              background: "#fff",
              zIndex: 10,
              padding: "12px",
              fontSize: 12,
              color: "#999",
              borderRadius: 4,
            }}
          >
            No users found
          </div>
        )}
      </div>
    );
  }
}

/* ---------------- Redux ---------------- */
const connector = connect(
  (state: State) => ({
    users: state.users.content?.data || [],
  }),
  { filterUsers }
);

type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(CopyUserFilter);
